import React from 'react';
import { Button, Modal } from 'rsuite';

export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmText: string;
  cancelText: string;
  destructive: boolean;
  resolve: ((value: boolean) => void) | null;
}

const INITIAL_STATE: ConfirmState = {
  open: false,
  title: 'Підтвердження',
  message: '',
  confirmText: 'Так',
  cancelText: 'Скасувати',
  destructive: false,
  resolve: null,
};

/**
 * RSuite-based replacement for `window.confirm`. Returns a `confirm` function
 * that resolves to true/false and a JSX dialog element to render somewhere
 * inside the component tree.
 *
 * Example:
 * ```tsx
 * const [confirm, ConfirmDialog] = useConfirm();
 * const onDelete = async () => {
 *   if (!(await confirm('Видалити запис?'))) return;
 *   // ...
 * };
 * return (<>
 *   <Button onClick={onDelete}>Delete</Button>
 *   {ConfirmDialog}
 * </>);
 * ```
 */
export function useConfirm(): [
  (opts: string | ConfirmOptions) => Promise<boolean>,
  React.ReactElement,
] {
  const [state, setState] = React.useState<ConfirmState>(INITIAL_STATE);

  const confirm = React.useCallback(
    (opts: string | ConfirmOptions): Promise<boolean> => {
      return new Promise<boolean>(resolve => {
        const normalized: ConfirmOptions =
          typeof opts === 'string' ? { message: opts } : opts;
        setState({
          open: true,
          title: normalized.title ?? 'Підтвердження',
          message: normalized.message,
          confirmText: normalized.confirmText ?? 'Так',
          cancelText: normalized.cancelText ?? 'Скасувати',
          destructive: normalized.destructive ?? false,
          resolve,
        });
      });
    },
    []
  );

  const handleClose = React.useCallback(
    (value: boolean) => {
      state.resolve?.(value);
      setState(s => ({ ...s, open: false, resolve: null }));
    },
    [state]
  );

  const dialog = (
    <Modal
      open={state.open}
      onClose={() => handleClose(false)}
      size="xs"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>{state.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{state.message}</Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => handleClose(true)}
          appearance="primary"
          color={state.destructive ? 'red' : undefined}
        >
          {state.confirmText}
        </Button>
        <Button onClick={() => handleClose(false)} appearance="subtle">
          {state.cancelText}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return [confirm, dialog];
}
